import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, Search, Plus, Eye, Edit3, Shield, AlertTriangle,
  CheckCircle, Clock, Award, XCircle, ChevronDown, ChevronUp,
  UserCheck, BookOpen, FileText, TrendingUp, Bell, Calendar,
  Briefcase, Mail, Phone, MapPin, X, Save, RefreshCw,
  AlertCircle, BarChart3, Download, Printer, Trash2, Send
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import HRService from '../../services/hr/HRService';
import { STAFF_ROLES, MANDATORY_TRAINING } from '../../data/models/StaffProfile';
import { TableSkeleton, CardSkeleton } from '../Common/SkeletonLoaders';
import { useToast } from '../Common/ToastProvider';
import { Tooltip, EmptyState } from '../Common/ReusableUI';
import { useConfirm } from '../Common/ConfirmDialog';

const SEVERITY_DESCRIPTIONS = {
  expired: 'Already expired — staff member is blocked from rostering until credential is renewed.',
  critical: 'Expires within 7 days — immediate renewal action required to avoid roster block.',
  warning: 'Expires within 30 days — schedule renewal soon to maintain compliance.',
  info: 'Expires within 60 days — plan ahead for upcoming renewal.',
};

const KPI_DESCRIPTIONS = {
  activeStaff: 'Number of staff currently on active duty and available for rostering.',
  onLeave: 'Staff currently on approved leave (annual, sick, parental, etc.).',
  credentialAlerts: 'Credentials approaching or past expiry that require attention.',
  agencyStaff: 'External agency staff currently engaged at this facility.',
  trainingCompliance: 'Average mandatory training completion rate across all modules.',
};

const COMPLIANCE_THRESHOLDS = '≥80% Green (compliant) · 60–79% Amber (at risk) · <60% Red (non-compliant)';

const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const HRModule = () => {
  const { state } = useAppContext();
  const { selectedFacility } = state;
  const { showToast } = useToast();
  const confirm = useConfirm();

  const [activeTab, setActiveTab] = useState('directory');
  const [staffList, setStaffList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [credentialAlerts, setCredentialAlerts] = useState(null);
  const [trainingReport, setTrainingReport] = useState(null);
  const [workforceStats, setWorkforceStats] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAlertIdxs, setSelectedAlertIdxs] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [staffResult, alertsResult, trainingResult, statsResult] = await Promise.all([
        HRService.getAll(selectedFacility),
        HRService.getAllCredentialAlerts(selectedFacility),
        HRService.getTrainingComplianceReport(selectedFacility),
        HRService.getWorkforceStats(selectedFacility)
      ]);

      if (staffResult.success) setStaffList(staffResult.data);
      if (alertsResult.success) setCredentialAlerts(alertsResult.data);
      if (trainingResult.success) setTrainingReport(trainingResult.data);
      if (statsResult.success) setWorkforceStats(statsResult.data);
    } catch (error) {
      console.error('[HRModule] Error loading data:', error);
      showToast('Failed to load HR data. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedFacility, showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredStaff = useMemo(() => {
    let filtered = staffList;
    if (filterRole !== 'all') filtered = filtered.filter(s => s.roleCode === filterRole);
    if (filterStatus !== 'all') filtered = filtered.filter(s => s.status === filterStatus);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        `${s.firstName} ${s.lastName}`.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.employeeNumber?.toLowerCase().includes(q) ||
        s.roleName?.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [staffList, filterRole, filterStatus, searchQuery]);

  const severityColor = (severity) => {
    switch (severity) {
      case 'expired': return 'bg-red-100 text-red-800 border-red-200';
      case 'critical': return 'bg-red-50 text-red-700 border-red-200';
      case 'warning': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-800';
      case 'on_leave': return 'bg-amber-100 text-amber-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'terminated': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleFilterRole = (value) => {
    setFilterRole(value);
    if (value !== 'all') {
      showToast(`Filtered by role: ${STAFF_ROLES[value]?.name || value}`, 'info', 2000);
    }
  };

  const handleFilterStatus = (value) => {
    setFilterStatus(value);
    if (value !== 'all') {
      showToast(`Filtered by status: ${value.replace('_', ' ')}`, 'info', 2000);
    }
  };

  const handleDeactivateStaff = async (staff) => {
    const confirmed = await confirm({
      title: 'Deactivate Staff Member',
      message: `Are you sure you want to deactivate ${staff.firstName} ${staff.lastName}? They will be removed from all active rosters and lose system access.`,
      confirmLabel: 'Deactivate',
      variant: 'danger',
    });
    if (confirmed) {
      showToast(`${staff.firstName} ${staff.lastName} has been deactivated`, 'success');
      loadData();
    }
  };

  const handleRemoveCredential = async (alert) => {
    const confirmed = await confirm({
      title: 'Remove Credential Record',
      message: `Remove the ${alert.credential} record for ${alert.staffName}? This action cannot be undone.`,
      confirmLabel: 'Remove',
      variant: 'danger',
    });
    if (confirmed) {
      showToast(`Credential record removed for ${alert.staffName}`, 'success');
    }
  };

  const handleChangeRole = async (staff) => {
    const confirmed = await confirm({
      title: 'Change Staff Role',
      message: `Changing the role for ${staff.firstName} ${staff.lastName} may affect their permissions, credential requirements, and roster assignments. Continue?`,
      confirmLabel: 'Change Role',
      variant: 'warning',
    });
    if (confirmed) {
      showToast(`Role change initiated for ${staff.firstName} ${staff.lastName}`, 'info');
    }
  };

  const exportStaffDirectoryCSV = () => {
    const headers = ['Employee Number', 'First Name', 'Last Name', 'Role', 'Facility', 'Employment Type', 'Status', 'Email', 'Phone', 'AHPRA Status', 'Screening Status'];
    const rows = filteredStaff.map(s => [
      s.employeeNumber, s.firstName, s.lastName, s.roleName,
      s.facilityName, s.employmentType, s.status, s.email,
      s.phone, s.ahpra?.status || 'N/A', s.workerScreening?.status || ''
    ]);
    const csv = [
      headers.join(','),
      ...rows.map(r => r.map(c => `"${String(c || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    downloadCSV(csv, `staff-directory-${new Date().toISOString().slice(0, 10)}.csv`);
    showToast(`Exported ${filteredStaff.length} staff records to CSV`, 'success');
  };

  const exportCredentialAlertsCSV = (alerts) => {
    const headers = ['Severity', 'Staff Member', 'Credential', 'Expiry Date', 'Days Remaining', 'Roster Blocked'];
    const rows = alerts.map(a => [
      a.severity, a.staffName, a.credential, a.expiryDate,
      a.daysUntilExpiry <= 0 ? 'EXPIRED' : `${a.daysUntilExpiry} days`,
      a.blockRostering ? 'Yes' : 'No'
    ]);
    const csv = [
      headers.join(','),
      ...rows.map(r => r.map(c => `"${String(c || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    downloadCSV(csv, `credential-alerts-${new Date().toISOString().slice(0, 10)}.csv`);
    showToast(`Exported ${alerts.length} credential alerts to CSV`, 'success');
  };

  const toggleAlertSelection = (idx) => {
    setSelectedAlertIdxs(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const toggleAllAlerts = () => {
    if (!credentialAlerts?.alerts) return;
    setSelectedAlertIdxs(prev =>
      prev.length === credentialAlerts.alerts.length
        ? []
        : credentialAlerts.alerts.map((_, i) => i)
    );
  };

  const handleBulkSendReminders = () => {
    const count = selectedAlertIdxs.length;
    showToast(`Renewal reminders sent to ${count} staff member${count !== 1 ? 's' : ''}`, 'success');
    setSelectedAlertIdxs([]);
  };

  const handleBulkExportSelected = () => {
    if (!credentialAlerts?.alerts) return;
    const selected = selectedAlertIdxs.map(i => credentialAlerts.alerts[i]).filter(Boolean);
    exportCredentialAlertsCSV(selected);
    setSelectedAlertIdxs([]);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-green-600 rounded-2xl p-8 text-white shadow-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <div className="w-20 h-20 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Briefcase className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">HR & Workforce Management</h1>
                <p className="text-teal-100 text-base mt-2">Loading staff profiles, credentials, and analytics...</p>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <TableSkeleton rows={8} columns={7} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-green-600 rounded-2xl p-8 text-white shadow-2xl">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Briefcase className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">HR & Workforce Management</h1>
              <p className="text-teal-100 text-base mt-2">Staff profiles, credentials, training compliance, and workforce analytics</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{workforceStats?.totalStaff || 0}</div>
            <div className="text-teal-100 text-sm">Total Staff</div>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 bg-white text-teal-600 px-6 py-3 rounded-xl font-semibold hover:bg-opacity-90 transition-all shadow-lg flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Staff Member
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards with Tooltips */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Active Staff', value: workforceStats?.active || 0, icon: UserCheck, color: 'emerald', sub: 'On duty', tip: KPI_DESCRIPTIONS.activeStaff },
          { label: 'On Leave', value: workforceStats?.onLeave || 0, icon: Calendar, color: 'amber', sub: 'Currently away', tip: KPI_DESCRIPTIONS.onLeave },
          { label: 'Credential Alerts', value: credentialAlerts?.alerts?.length || 0, icon: AlertTriangle, color: 'red', sub: `${credentialAlerts?.expired?.length || 0} expired`, tip: KPI_DESCRIPTIONS.credentialAlerts },
          { label: 'Agency Staff', value: workforceStats?.agency || 0, icon: Users, color: 'blue', sub: 'External workers', tip: KPI_DESCRIPTIONS.agencyStaff },
          { label: 'Training Compliance', value: `${trainingReport ? Math.round(trainingReport.reduce((s, t) => s + parseFloat(t.complianceRate), 0) / trainingReport.length) : 0}%`, icon: Award, color: 'purple', sub: 'Average rate', tip: KPI_DESCRIPTIONS.trainingCompliance }
        ].map(stat => {
          const Icon = stat.icon;
          return (
            <Tooltip key={stat.label} content={stat.tip} position="bottom">
              <div className={`bg-gradient-to-br from-${stat.color}-50 to-${stat.color}-100 rounded-2xl p-5 border border-${stat.color}-200 shadow-lg cursor-help`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-${stat.color}-600 text-xs font-medium`}>{stat.label}</p>
                    <p className={`text-2xl font-bold text-${stat.color}-900 mt-1`}>{stat.value}</p>
                    <p className="text-xs text-gray-500 mt-1">{stat.sub}</p>
                  </div>
                  <div className={`w-12 h-12 bg-gradient-to-br from-${stat.color}-500 to-${stat.color}-600 rounded-xl flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
        <div className="flex space-x-1 p-1 overflow-x-auto">
          {[
            { id: 'directory', label: 'Staff Directory', icon: Users },
            { id: 'credentials', label: 'Credential Tracking', icon: Shield },
            { id: 'training', label: 'Training Register', icon: BookOpen },
            { id: 'analytics', label: 'Workforce Analytics', icon: BarChart3 }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {/* Staff Directory Tab */}
          {activeTab === 'directory' && (
            <div className="space-y-4">
              {/* Filters + Export/Print */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex flex-col lg:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search staff by name, email, or employee number..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                    />
                  </div>
                  <select value={filterRole} onChange={e => handleFilterRole(e.target.value)} className="px-3 py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-teal-500">
                    <option value="all">All Roles</option>
                    {Object.entries(STAFF_ROLES).map(([code, role]) => (
                      <option key={code} value={code}>{role.name}</option>
                    ))}
                  </select>
                  <select value={filterStatus} onChange={e => handleFilterStatus(e.target.value)} className="px-3 py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-teal-500">
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="on_leave">On Leave</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <button onClick={exportStaffDirectoryCSV} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-sm" title="Export to CSV">
                    <Download className="w-4 h-4" /> Export CSV
                  </button>
                  <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-sm" title="Print staff directory">
                    <Printer className="w-4 h-4" /> Print
                  </button>
                </div>
              </div>

              {/* Staff Table */}
              {filteredStaff.length === 0 ? (
                <EmptyState
                  icon={Search}
                  title="No staff members found"
                  description={searchQuery || filterRole !== 'all' || filterStatus !== 'all'
                    ? 'Try adjusting your search query or filters to find staff members.'
                    : 'No staff members have been added yet. Click "Add Staff Member" to get started.'}
                />
              ) : (
                <>
                  <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                    <table className="min-w-full text-left text-sm">
                      <thead className="bg-gray-50 text-xs font-bold uppercase text-gray-600">
                        <tr>
                          <th className="px-4 py-3">Staff Member</th>
                          <th className="px-4 py-3">Role</th>
                          <th className="px-4 py-3">Employment</th>
                          <th className="px-4 py-3">AHPRA</th>
                          <th className="px-4 py-3">Screening</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStaff.map(staff => (
                          <tr key={staff.id} className="border-t border-gray-100 hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white font-bold text-sm">
                                  {staff.firstName?.[0]}{staff.lastName?.[0]}
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{staff.firstName} {staff.lastName}</div>
                                  <div className="text-xs text-gray-500">{staff.employeeNumber}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium">{staff.roleName}</span>
                              <div className="text-xs text-gray-500">{staff.facilityName}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                staff.employmentType === 'permanent' ? 'bg-green-100 text-green-800' :
                                staff.employmentType === 'agency' ? 'bg-orange-100 text-orange-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {staff.employmentType}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {staff.ahpra?.status === 'not_applicable' ? (
                                <span className="text-xs text-gray-400">N/A</span>
                              ) : (
                                <div>
                                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                    staff.ahpra?.status === 'current' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {staff.ahpra?.status}
                                  </span>
                                  {staff.ahpra?.expiryDate && (
                                    <div className="text-xs text-gray-500 mt-0.5">{staff.ahpra.expiryDate}</div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                staff.workerScreening?.status === 'cleared' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                              }`}>
                                {staff.workerScreening?.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-bold uppercase ${statusBadge(staff.status)}`}>
                                {staff.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <button onClick={() => setSelectedStaff(staff)} className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg" title="View Profile">
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleChangeRole(staff)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Change Role">
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                {staff.status === 'active' && (
                                  <button onClick={() => handleDeactivateStaff(staff)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Deactivate">
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-sm text-gray-500">
                    Showing {filteredStaff.length} of {staffList.length} staff members
                  </div>
                </>
              )}
            </div>
          )}

          {/* Credential Tracking Tab */}
          {activeTab === 'credentials' && credentialAlerts && (
            <div className="space-y-6">
              {/* Alert Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                  { label: 'Expired', count: credentialAlerts.expired.length, color: 'red', icon: XCircle, tip: SEVERITY_DESCRIPTIONS.expired },
                  { label: 'Critical (7 days)', count: credentialAlerts.critical.length, color: 'red', icon: AlertCircle, tip: SEVERITY_DESCRIPTIONS.critical },
                  { label: 'Warning (30 days)', count: credentialAlerts.warning.length, color: 'amber', icon: AlertTriangle, tip: SEVERITY_DESCRIPTIONS.warning },
                  { label: 'Info (60 days)', count: credentialAlerts.info.length, color: 'blue', icon: Bell, tip: SEVERITY_DESCRIPTIONS.info }
                ].map(item => {
                  const Icon = item.icon;
                  return (
                    <Tooltip key={item.label} content={item.tip} position="bottom">
                      <div className={`bg-${item.color}-50 border border-${item.color}-200 rounded-xl p-4 cursor-help`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-${item.color}-700 text-sm font-medium`}>{item.label}</p>
                            <p className={`text-2xl font-bold text-${item.color}-900 mt-1`}>{item.count}</p>
                          </div>
                          <Icon className={`w-8 h-8 text-${item.color}-400`} />
                        </div>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>

              {credentialAlerts.staffBlocked.length > 0 && (
                <div className="bg-red-50 border border-red-300 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
                    <Shield className="w-5 h-5" />
                    Roster-Blocked Staff ({credentialAlerts.staffBlocked.length})
                  </div>
                  <p className="text-sm text-red-700">
                    These staff members have expired credentials and are automatically blocked from rostering until renewed.
                  </p>
                </div>
              )}

              {/* Bulk Action Bar */}
              {selectedAlertIdxs.length > 0 && (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex items-center justify-between">
                  <span className="text-sm font-medium text-teal-800">
                    {selectedAlertIdxs.length} credential alert{selectedAlertIdxs.length !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex gap-2">
                    <button onClick={handleBulkSendReminders} className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition">
                      <Send className="w-4 h-4" /> Send Reminders
                    </button>
                    <button onClick={handleBulkExportSelected} className="flex items-center gap-1.5 px-4 py-2 bg-white border border-teal-300 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-50 transition">
                      <Download className="w-4 h-4" /> Export Selected
                    </button>
                    <button onClick={() => setSelectedAlertIdxs([])} className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Export all button */}
              <div className="flex justify-end gap-2">
                <button onClick={() => exportCredentialAlertsCSV(credentialAlerts.alerts)} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-sm">
                  <Download className="w-4 h-4" /> Export All Alerts
                </button>
                <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-sm">
                  <Printer className="w-4 h-4" /> Print Report
                </button>
              </div>

              {/* Alerts Table with Checkboxes */}
              {credentialAlerts.alerts.length === 0 ? (
                <EmptyState
                  icon={CheckCircle}
                  title="No credential alerts"
                  description="All staff credentials are current and compliant. Great work keeping everything up to date!"
                />
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-gray-50 text-xs font-bold uppercase text-gray-600">
                      <tr>
                        <th className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedAlertIdxs.length === credentialAlerts.alerts.length && credentialAlerts.alerts.length > 0}
                            onChange={toggleAllAlerts}
                            className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          />
                        </th>
                        <th className="px-4 py-3">Severity</th>
                        <th className="px-4 py-3">Staff Member</th>
                        <th className="px-4 py-3">Credential</th>
                        <th className="px-4 py-3">Expiry Date</th>
                        <th className="px-4 py-3">Days Remaining</th>
                        <th className="px-4 py-3">Roster Block</th>
                        <th className="px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credentialAlerts.alerts.map((alert, idx) => (
                        <tr key={idx} className={`border-t border-gray-100 hover:bg-gray-50 ${selectedAlertIdxs.includes(idx) ? 'bg-teal-50/50' : ''}`}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedAlertIdxs.includes(idx)}
                              onChange={() => toggleAlertSelection(idx)}
                              className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Tooltip content={SEVERITY_DESCRIPTIONS[alert.severity] || 'No description available'} position="right">
                              <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-bold uppercase cursor-help ${severityColor(alert.severity)}`}>
                                {alert.severity}
                              </span>
                            </Tooltip>
                          </td>
                          <td className="px-4 py-3 font-medium">{alert.staffName}</td>
                          <td className="px-4 py-3">{alert.credential}</td>
                          <td className="px-4 py-3">{alert.expiryDate}</td>
                          <td className="px-4 py-3">
                            <span className={alert.daysUntilExpiry <= 0 ? 'text-red-600 font-bold' : alert.daysUntilExpiry <= 7 ? 'text-red-600' : 'text-gray-900'}>
                              {alert.daysUntilExpiry <= 0 ? 'EXPIRED' : `${alert.daysUntilExpiry} days`}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {alert.blockRostering ? (
                              <span className="inline-flex items-center gap-1 text-red-600 text-xs font-bold"><XCircle className="w-3.5 h-3.5" /> Blocked</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-green-600 text-xs"><CheckCircle className="w-3.5 h-3.5" /> OK</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleRemoveCredential(alert)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Remove credential record">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Training Register Tab */}
          {activeTab === 'training' && trainingReport && (
            <div className="space-y-6">
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex items-center justify-between">
                <p className="text-sm text-teal-800">
                  <strong>Mandatory Training Register</strong> — Tracks completion and compliance for all required training modules across staff.
                </p>
                <Tooltip content={COMPLIANCE_THRESHOLDS} position="left">
                  <span className="text-xs text-teal-600 border border-teal-300 rounded-full px-3 py-1 cursor-help whitespace-nowrap">
                    Threshold Guide
                  </span>
                </Tooltip>
              </div>

              {trainingReport.length === 0 ? (
                <EmptyState
                  icon={BookOpen}
                  title="No training records"
                  description="No mandatory training modules have been configured yet. Set up training requirements to begin tracking compliance."
                />
              ) : (
                <div className="space-y-4">
                  {trainingReport.map(training => (
                    <TrainingRow key={training.trainingId} training={training} showToast={showToast} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Workforce Analytics Tab */}
          {activeTab === 'analytics' && workforceStats && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-sm">
                  <Printer className="w-4 h-4" /> Print Analytics
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Role Distribution */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-teal-600" />
                    Staff by Role
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(workforceStats.byRole)
                      .filter(([, count]) => count > 0)
                      .sort((a, b) => b[1] - a[1])
                      .map(([role, count]) => (
                        <div key={role} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-teal-500" />
                            <span className="text-sm font-medium">{STAFF_ROLES[role]?.name || role}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Tooltip content={`${((count / workforceStats.totalStaff) * 100).toFixed(1)}% of total workforce`} position="left">
                              <div className="flex items-center gap-2 cursor-help">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${(count / workforceStats.totalStaff) * 100}%` }} />
                                </div>
                                <span className="text-sm font-bold text-gray-700 w-8 text-right">{count}</span>
                              </div>
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Employment Type */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-teal-600" />
                    Employment Type
                  </h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Permanent', value: workforceStats.permanent, color: 'green' },
                      { label: 'Casual', value: workforceStats.casual, color: 'blue' },
                      { label: 'Agency', value: workforceStats.agency, color: 'orange' },
                      { label: 'Contract', value: workforceStats.contract, color: 'purple' }
                    ].map(type => (
                      <div key={type.label} className={`flex items-center justify-between p-3 bg-${type.color}-50 rounded-lg`}>
                        <span className="text-sm font-medium">{type.label}</span>
                        <div className="flex items-center gap-3">
                          <span className={`text-xl font-bold text-${type.color}-700`}>{type.value}</span>
                          <span className="text-xs text-gray-500">
                            ({workforceStats.totalStaff > 0 ? ((type.value / workforceStats.totalStaff) * 100).toFixed(0) : 0}%)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Credential Health */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-teal-600" />
                    Credential Health
                  </h3>
                  {workforceStats.credentialAlerts ? (
                    <div className="space-y-3">
                      <Tooltip content={SEVERITY_DESCRIPTIONS.expired} position="right">
                        <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg cursor-help">
                          <span className="text-sm font-medium text-red-700">Expired</span>
                          <span className="text-xl font-bold text-red-700">{workforceStats.credentialAlerts.expired}</span>
                        </div>
                      </Tooltip>
                      <Tooltip content={SEVERITY_DESCRIPTIONS.critical} position="right">
                        <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg cursor-help">
                          <span className="text-sm font-medium text-amber-700">Critical Alerts</span>
                          <span className="text-xl font-bold text-amber-700">{workforceStats.credentialAlerts.critical}</span>
                        </div>
                      </Tooltip>
                      <Tooltip content={SEVERITY_DESCRIPTIONS.warning} position="right">
                        <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg cursor-help">
                          <span className="text-sm font-medium text-blue-700">Warnings</span>
                          <span className="text-xl font-bold text-blue-700">{workforceStats.credentialAlerts.warning}</span>
                        </div>
                      </Tooltip>
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Blocked from Roster</span>
                        <span className="text-xl font-bold text-gray-700">{workforceStats.credentialAlerts.blockedStaff}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No credential data available.</p>
                  )}
                </div>

                {/* Training Overview */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-teal-600" />
                    Training Compliance Overview
                  </h3>
                  {trainingReport && (
                    <div className="space-y-2">
                      {trainingReport.map(t => (
                        <Tooltip key={t.trainingId} content={`${t.completed} completed · ${t.expired} expired · ${t.notCompleted} pending`} position="left">
                          <div className="flex items-center justify-between text-sm cursor-help">
                            <span className="truncate mr-2">{t.trainingName}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full ${parseFloat(t.complianceRate) >= 80 ? 'bg-green-500' : parseFloat(t.complianceRate) >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                                  style={{ width: `${t.complianceRate}%` }}
                                />
                              </div>
                              <span className="text-xs font-bold w-10 text-right">{t.complianceRate}%</span>
                            </div>
                          </div>
                        </Tooltip>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Staff Detail Modal */}
      {selectedStaff && (
        <StaffDetailModal
          staff={selectedStaff}
          onClose={() => setSelectedStaff(null)}
          onDeactivate={handleDeactivateStaff}
          onChangeRole={handleChangeRole}
        />
      )}

      {/* Add Staff Modal */}
      {showAddModal && (
        <AddStaffModal
          onClose={() => setShowAddModal(false)}
          onSave={async (data) => {
            await HRService.create(data);
            setShowAddModal(false);
            showToast(`${data.firstName} ${data.lastName} added to staff directory`, 'success');
            loadData();
          }}
        />
      )}
    </div>
  );
};

const TrainingRow = ({ training, showToast }) => {
  const [expanded, setExpanded] = useState(false);
  const complianceRate = parseFloat(training.complianceRate);

  const handleMarkComplete = (record) => {
    showToast(`Training "${training.trainingName}" marked complete for ${record.staffName}`, 'success');
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors text-left">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-teal-600" />
          <div>
            <span className="font-semibold text-gray-900">{training.trainingName}</span>
            <span className="ml-2 text-xs text-gray-500">({training.frequency})</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Tooltip content={COMPLIANCE_THRESHOLDS} position="left">
              <div className="flex items-center gap-2 cursor-help">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div className={`h-2 rounded-full ${complianceRate >= 80 ? 'bg-green-500' : complianceRate >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${complianceRate}%` }} />
                </div>
                <span className="text-sm font-bold">{training.complianceRate}%</span>
              </div>
            </Tooltip>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full">{training.completed} done</span>
            <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full">{training.expired} expired</span>
            <span className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full">{training.notCompleted} pending</span>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-gray-500 uppercase">
                <th className="text-left py-1">Staff</th>
                <th className="text-left py-1">Role</th>
                <th className="text-left py-1">Status</th>
                <th className="text-left py-1">Completed</th>
                <th className="text-left py-1">Expires</th>
                <th className="text-left py-1">Score</th>
                <th className="text-left py-1">Action</th>
              </tr>
            </thead>
            <tbody>
              {training.staffRecords.map(record => (
                <tr key={record.staffId} className="border-t border-gray-100">
                  <td className="py-1.5 font-medium">{record.staffName}</td>
                  <td className="py-1.5">{record.roleCode}</td>
                  <td className="py-1.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      record.status === 'completed' ? 'bg-green-100 text-green-800' : record.status === 'expired' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                    }`}>{record.status}</span>
                  </td>
                  <td className="py-1.5">{record.completedDate || '—'}</td>
                  <td className="py-1.5">{record.expiryDate || '—'}</td>
                  <td className="py-1.5">{record.score ? `${record.score}%` : '—'}</td>
                  <td className="py-1.5">
                    {record.status !== 'completed' && (
                      <button
                        onClick={() => handleMarkComplete(record)}
                        className="text-teal-600 hover:text-teal-800 text-[10px] font-medium underline"
                      >
                        Mark Complete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StaffDetailModal = ({ staff, onClose, onDeactivate, onChangeRole }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-2xl font-bold">
                {staff.firstName?.[0]}{staff.lastName?.[0]}
              </div>
              <div>
                <h2 className="text-2xl font-bold">{staff.firstName} {staff.lastName}</h2>
                <p className="text-teal-100">{staff.roleName} — {staff.employeeNumber}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {staff.status === 'active' && (
                <>
                  <button onClick={() => { onChangeRole(staff); }} className="px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg text-sm font-medium transition flex items-center gap-1">
                    <Edit3 className="w-4 h-4" /> Change Role
                  </button>
                  <button onClick={() => { onDeactivate(staff); }} className="px-3 py-1.5 bg-red-500 bg-opacity-80 hover:bg-opacity-100 text-white rounded-lg text-sm font-medium transition flex items-center gap-1">
                    <XCircle className="w-4 h-4" /> Deactivate
                  </button>
                </>
              )}
              <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg"><X className="w-6 h-6" /></button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Contact */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-gray-400" />{staff.email || 'No email'}</div>
              <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-gray-400" />{staff.phone || 'No phone'}</div>
              <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-gray-400" />{staff.facilityName}</div>
              <div className="flex items-center gap-2 text-sm"><Briefcase className="w-4 h-4 text-gray-400" />{staff.employmentType} ({staff.employmentStatus})</div>
            </div>
          </div>

          {/* Credentials */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Credentials & Compliance</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {staff.ahpra?.status !== 'not_applicable' && (
                <div className={`p-3 rounded-lg border ${staff.ahpra?.status === 'current' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="text-xs text-gray-500 mb-1">AHPRA Registration</div>
                  <div className="font-medium text-sm">{staff.ahpra?.registrationNumber}</div>
                  <div className="text-xs mt-1">Expires: {staff.ahpra?.expiryDate}</div>
                </div>
              )}
              <div className={`p-3 rounded-lg border ${staff.workerScreening?.status === 'cleared' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="text-xs text-gray-500 mb-1">Worker Screening</div>
                <div className="font-medium text-sm">{staff.workerScreening?.clearanceNumber}</div>
                <div className="text-xs mt-1">Expires: {staff.workerScreening?.expiryDate}</div>
              </div>
              <div className={`p-3 rounded-lg border ${staff.policeCheck?.status === 'cleared' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="text-xs text-gray-500 mb-1">Police Check</div>
                <div className="font-medium text-sm">{staff.policeCheck?.referenceNumber}</div>
                <div className="text-xs mt-1">Expires: {staff.policeCheck?.expiryDate}</div>
              </div>
              <div className="p-3 rounded-lg border bg-green-50 border-green-200">
                <div className="text-xs text-gray-500 mb-1">Right to Work</div>
                <div className="font-medium text-sm">{staff.rightToWork?.documentType}</div>
                <div className="text-xs mt-1">Verified: {staff.rightToWork?.verified ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>

          {/* Qualifications */}
          {staff.qualifications?.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Qualifications</h3>
              <div className="space-y-2">
                {staff.qualifications.map((q, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2"><Award className="w-4 h-4 text-teal-600" />{q.name}</div>
                    <span className="text-gray-500">{q.institution}, {q.year}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Training */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Mandatory Training</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {staff.trainingRecords?.map(t => (
                <div key={t.trainingId} className={`p-2 rounded-lg border text-xs ${t.status === 'completed' ? 'bg-green-50 border-green-200' : t.status === 'expired' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="font-medium">{t.trainingName}</div>
                  <div className="flex justify-between mt-1 text-gray-500">
                    <span>{t.completedDate || 'Not completed'}</span>
                    <span className={`font-bold uppercase ${t.status === 'completed' ? 'text-green-700' : t.status === 'expired' ? 'text-red-700' : 'text-gray-500'}`}>{t.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddStaffModal = ({ onClose, onSave }) => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', roleCode: 'PCA',
    facilityId: 'sunshine', facilityName: 'Sunshine Gardens',
    employmentType: 'permanent', employmentStatus: 'full_time'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      roleName: STAFF_ROLES[form.roleCode]?.name || form.roleCode,
      status: 'active'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Add Staff Member</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input type="text" required value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input type="text" required value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select value={form.roleCode} onChange={e => setForm({ ...form, roleCode: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500">
              {Object.entries(STAFF_ROLES).map(([code, role]) => (
                <option key={code} value={code}>{role.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
              <select value={form.employmentType} onChange={e => setForm({ ...form, employmentType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500">
                <option value="permanent">Permanent</option>
                <option value="casual">Casual</option>
                <option value="contract">Contract</option>
                <option value="agency">Agency</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.employmentStatus} onChange={e => setForm({ ...form, employmentStatus: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500">
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="casual">Casual</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg hover:from-teal-600 hover:to-emerald-700 font-medium flex items-center justify-center gap-2">
              <Save className="w-4 h-4" /> Save Staff Member
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HRModule;
